import { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import * as api from "../api/client";

const EMPTY = { name: "", kind: "", host: "", port: "", database: "", username: "", password: "", file: "",
  auth_source: "", tls: false };

const BRAND = {
  postgresql: ["PG", "#336791"], mysql: ["My", "#00758F"], mariadb: ["Ma", "#A3624A"], mssql: ["MS", "#C8102E"],
  oracle: ["Or", "#E2231A"], sqlite: ["SQ", "#0F5A80"], mongodb: ["Mg", "#13AA52"],
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const suggestTarget = (conn, table) =>
  ("ext_" + [slug(conn).slice(0, 20), slug(table).slice(0, 34)].filter(Boolean).join("_")).slice(0, 59);
const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));
const fmtTime = (s) => (s ? new Date(s * 1000).toLocaleString() : "—");

const PATHS = {
  db: "M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zm0 0v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3",
  schema: "M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
  table: "M3 5h18v14H3zM3 10h18M3 15h18M9 10v9",
  view: "M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  collection: "M4 7h16M4 12h16M4 17h10",
  chevron: "M9 6l6 6-6 6",
  trash: "M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13",
  plus: "M12 5v14M5 12h14",
  check: "M5 12.5l4.5 4.5L19 7.5",
};

function Icon({ name, size = 16, className = "" }) {
  return (
    <svg className={`cx-svg ${className}`} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={PATHS[name]} />
    </svg>
  );
}

function EngineBadge({ kind, size = 28 }) {
  const [txt, color] = BRAND[kind] || ["DB", "#64748b"];
  return <span className="cx-engine" style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}>{txt}</span>;
}

export default function ConnectionsPage() {
  const [connections, setConnections] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [files, setFiles] = useState({ folder: "", files: [] });
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [formMsg, setFormMsg] = useState(null);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);

  const loadImports = useCallback(async () => {
    try {
      setImports((await api.listImports()).imports || []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, k, f] = await Promise.all([api.listConnections(), api.listConnectionKinds(), api.listSqliteFiles()]);
      setConnections(c.connections || []);
      setKinds(k.kinds || []);
      setFiles(f);
      await loadImports();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [loadImports]);

  useEffect(() => {
    load();
  }, [load]);

  const running = imports.some((i) => i.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(loadImports, 1500);
    return () => clearInterval(t);
  }, [running, loadImports]);

  const open = !!form;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && !busy && setForm(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy]);

  const kind = kinds.find((k) => k.id === form?.kind);
  const has = (f) => !!kind && kind.fields.includes(f);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  function pickKind(id) {
    const k = kinds.find((x) => x.id === id);
    setForm((f) => ({ ...f, kind: id, port: k?.port || "" }));
    setFormMsg(null);
  }

  const spec = () => ({ ...form, port: Number(form.port) || undefined });
  const canSave = form && form.kind && form.name.trim() && (form.kind === "sqlite" ? !!form.file : !!form.host);

  async function runTest() {
    setBusy("test");
    setFormMsg(null);
    try {
      const r = await api.testConnection(spec());
      setFormMsg({ ok: true, text: `Connected · ${r.version}` });
    } catch (e) {
      setFormMsg({ ok: false, text: e.message });
    } finally {
      setBusy("");
    }
  }

  async function save(e) {
    e.preventDefault();
    setBusy("save");
    setFormMsg(null);
    try {
      await api.createConnection(spec());
      setForm(null);
      await load();
    } catch (err) {
      setFormMsg({ ok: false, text: err.message });
    } finally {
      setBusy("");
    }
  }

  async function remove(c) {
    if (!window.confirm(`Remove the connection "${c.name}"? The database itself is not touched, and data already imported stays in QLens.`)) return;
    try {
      await api.deleteConnection(c.id);
      if (selected?.conn.id === c.id) setSelected(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function refreshImp(i) {
    try {
      await api.refreshImport(i.id);
      await loadImports();
    } catch (e) {
      setError(e.message);
    }
  }

  async function removeImp(i) {
    if (!window.confirm(`Remove "${i.table}" from QLens? Chats and dashboards can no longer use it.`)) return;
    try {
      const r = await api.deleteImport(i.id);
      if (r.rolesStillGranting?.length) setError(`Removed. These roles still list it: ${r.rolesStillGranting.join(", ")}.`);
      await loadImports();
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <div className="cx-page">
      <header className="cx-head">
        <div>
          <h1 className="cx-title">Connections</h1>
          <p className="cx-sub">Browse your QLens workspace and external databases. Import external tables to ask questions about them. Every connection is read-only.</p>
        </div>
        <button type="button" className="cx-btn primary" onClick={() => { setForm({ ...EMPTY }); setFormMsg(null); }}>
          <Icon name="plus" /> Add connection
        </button>
      </header>

      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

      {loading ? (
        <div className="cx-card cx-empty"><Spinner animation="border" size="sm" /></div>
      ) : (
        <div className="cx-layout">
          <aside className="cx-card cx-tree" aria-label="Catalog">
            <div className="cx-card-h">Catalog</div>
            {connections.map((c) => (
              <ConnectionNode key={c.id} conn={c} selected={selected} onSelect={setSelected} onRemove={() => remove(c)} />
            ))}
          </aside>
          <section className="cx-card cx-detail">
            {selected ? (
              <TableDetail key={JSON.stringify([selected.conn.id, selected.database, selected.schema, selected.table])}
                           sel={selected} onImported={loadImports} />
            ) : (
              <div className="cx-empty">
                <span className="cx-empty-ic"><Icon name="table" size={22} /></span>
                <strong>Pick a table from the catalog</strong>
                <span>You'll see its columns, sample rows and, for external databases, the import option.</span>
              </div>
            )}
          </section>
        </div>
      )}

      {imports.length > 0 && (
        <section className="cx-card cx-imports">
          <div className="cx-card-h">Imported into QLens</div>
          <div className="cx-scroll">
            <table className="cx-table">
              <thead><tr><th>QLens table</th><th>Source</th><th>Rows</th><th>Status</th><th>Updated</th><th /></tr></thead>
              <tbody>
                {imports.map((i) => (
                  <tr key={i.id}>
                    <td><code>{i.table}</code></td>
                    <td>{i.connectionName} › {i.source.database} › {i.source.table}</td>
                    <td>{fmtNum(i.rows)}{i.truncated ? " (capped)" : ""}</td>
                    <td>
                      <span className={`cx-status ${i.status}`}>{i.status === "ok" ? "Ready" : i.status}</span>
                      {i.error && <div className="cx-err">{i.error}</div>}
                    </td>
                    <td className="cx-muted">{fmtTime(i.finishedAt || i.startedAt)}</td>
                    <td className="cx-actions">
                      <button type="button" className="cx-link" disabled={i.status === "running"} onClick={() => refreshImp(i)}>Refresh</button>
                      <button type="button" className="cx-link danger" disabled={i.status === "running"} onClick={() => removeImp(i)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {form && (
        <div className="modal-backdrop" onClick={() => !busy && setForm(null)}>
          <div className="cx-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="New connection">
            <div className="cx-modal-h">
              <div>
                <div className="cx-modal-title">New connection</div>
                <div className="cx-muted">{kind ? `${kind.label} · step 2 of 2` : "Choose a database · step 1 of 2"}</div>
              </div>
              <button type="button" className="cx-x-btn" onClick={() => setForm(null)} aria-label="Close">×</button>
            </div>

            {!kind ? (
              <div className="cx-modal-body">
                <div className="cx-engines">
                  {kinds.map((k) => (
                    <button key={k.id} type="button" className="cx-engine-card" onClick={() => pickKind(k.id)}>
                      <EngineBadge kind={k.id} size={36} />
                      <span className="cx-engine-txt">
                        <span className="cx-engine-name">{k.label}</span>
                        <span className={`cx-pill ${k.status}`}>{k.status === "verified" ? "Verified" : "Beta"}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <form onSubmit={save} className="cx-form-wrap">
                <div className="cx-modal-body">
                  <div className="cx-chosen">
                    <EngineBadge kind={kind.id} size={32} />
                    <div>
                      <strong>{kind.label}</strong>
                      <div className="cx-muted">{kind.help}</div>
                    </div>
                    <button type="button" className="cx-link" onClick={() => setForm((f) => ({ ...f, kind: "" }))}>Change</button>
                  </div>
                  {kind.status === "beta" && (
                    <div className="cx-beta">Beta: not yet verified against a live server. Run “Test connection” before saving.</div>
                  )}
                  <div className="cx-grid">
                    <label className="cx-field span2">
                      <span>Connection name</span>
                      <input value={form.name} onChange={set("name")} placeholder="e.g. Sales warehouse" maxLength={80} required />
                    </label>
                    {has("file") && (
                      <label className="cx-field span2">
                        <span>Database file</span>
                        <select value={form.file} onChange={set("file")} required>
                          <option value="">Choose a file…</option>
                          {files.files.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                        <small>From the server folder {files.folder}</small>
                      </label>
                    )}
                    {has("host") && (
                      <>
                        <label className="cx-field">
                          <span>Host</span>
                          <input value={form.host} onChange={set("host")} placeholder="db.example.com" required />
                        </label>
                        <label className="cx-field">
                          <span>Port</span>
                          <input type="number" value={form.port} onChange={set("port")} />
                        </label>
                      </>
                    )}
                    {has("database") && (
                      <label className="cx-field span2">
                        <span>{kind.databaseLabel}{kind.id !== "oracle" && <em> · optional</em>}</span>
                        <input value={form.database} onChange={set("database")} required={kind.id === "oracle"}
                               placeholder={kind.id === "oracle" ? "e.g. FREEPDB1" : "Leave blank to list every database"} />
                      </label>
                    )}
                    {has("username") && (
                      <>
                        <label className="cx-field">
                          <span>Username{kind.family === "mongo" && <em> · optional</em>}</span>
                          <input value={form.username} onChange={set("username")} autoComplete="off" required={kind.family === "sql"} />
                        </label>
                        <label className="cx-field">
                          <span>Password</span>
                          <input type="password" value={form.password} onChange={set("password")} autoComplete="new-password" />
                        </label>
                      </>
                    )}
                    {has("auth_source") && (
                      <>
                        <label className="cx-field">
                          <span>Auth database<em> · optional</em></span>
                          <input value={form.auth_source} onChange={set("auth_source")} placeholder="admin" />
                        </label>
                        <label className="cx-check">
                          <input type="checkbox" checked={form.tls} onChange={set("tls")} /> Use TLS
                        </label>
                      </>
                    )}
                  </div>
                  <p className="cx-muted cx-note">Use a read-only database user. Passwords are encrypted at rest and never shown again.</p>
                  {formMsg && <div className={`cx-msgbox ${formMsg.ok ? "ok" : "bad"}`}>{formMsg.ok && <Icon name="check" />}{formMsg.text}</div>}
                </div>
                <div className="cx-modal-f">
                  <button type="button" className="cx-btn" disabled={!!busy || !canSave} onClick={runTest}>
                    {busy === "test" ? <Spinner size="sm" animation="border" /> : "Test connection"}
                  </button>
                  <button type="submit" className="cx-btn primary" disabled={!!busy || !canSave}>
                    {busy === "save" ? <Spinner size="sm" animation="border" /> : "Save connection"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Node({ label, sub, icon, depth, load, children, actions, active, onClick, badge }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ status: "idle", items: null, error: "" });
  const expandable = !!load;

  const fetchChildren = useCallback(async () => {
    setState({ status: "loading", items: null, error: "" });
    try {
      setState({ status: "ready", items: await load(), error: "" });
    } catch (e) {
      setState({ status: "error", items: null, error: e.message });
    }
  }, [load]);

  function toggle() {
    if (onClick) onClick();
    if (!expandable) return;
    const next = !open;
    setOpen(next);
    if (next && state.status === "idle") fetchChildren();
  }

  const indent = { paddingLeft: 12 + depth * 18 };
  return (
    <div>
      <div className={"cx-node" + (active ? " active" : "")} style={indent}>
        <button type="button" className="cx-node-btn" onClick={toggle} aria-expanded={expandable ? open : undefined}>
          <span className={"cx-caret" + (open ? " open" : "")}>{expandable && <Icon name="chevron" size={12} />}</span>
          {icon}
          <span className="cx-node-text">
            <span className="cx-label">{label}</span>
            {sub && <span className="cx-hint">{sub}</span>}
          </span>
          {badge}
        </button>
        {actions}
      </div>
      {open && state.status === "loading" && <div className="cx-msg" style={{ paddingLeft: 40 + depth * 18 }}>Loading…</div>}
      {open && state.status === "error" && (
        <div className="cx-msg err" style={{ paddingLeft: 40 + depth * 18 }}>
          {state.error} <button type="button" className="cx-link" onClick={fetchChildren}>Retry</button>
        </div>
      )}
      {open && state.status === "ready" &&
        (state.items.length === 0 ? <div className="cx-msg" style={{ paddingLeft: 40 + depth * 18 }}>Empty</div> : children(state.items))}
    </div>
  );
}

function ConnectionNode({ conn, selected, onSelect, onRemove }) {
  const loadDbs = useCallback(async () => (await api.listConnectionDatabases(conn.id)).databases, [conn.id]);
  const badge = conn.builtin ? <span className="cx-pill builtin">Built-in</span>
    : conn.status === "beta" ? <span className="cx-pill beta">Beta</span> : null;
  return (
    <Node depth={0} icon={<EngineBadge kind={conn.kind} size={24} />} label={conn.name}
          sub={`${conn.kindLabel}${conn.host ? " · " + conn.host : ""}`} badge={badge} load={loadDbs}
          actions={!conn.builtin && (
            <button type="button" className="cx-del" title="Remove connection" onClick={onRemove}><Icon name="trash" size={14} /></button>
          )}>
      {(dbs) => dbs.map((db) => <DatabaseNode key={db} conn={conn} db={db} selected={selected} onSelect={onSelect} />)}
    </Node>
  );
}

function DatabaseNode({ conn, db, selected, onSelect }) {
  const loadTree = useCallback(async () => (await api.getConnectionTree(conn.id, db)).schemas, [conn.id, db]);
  return (
    <Node depth={1} icon={<Icon name="db" className="ic-db" />} label={db} load={loadTree}>
      {(schemas) => schemas.map((s) => <SchemaNode key={s.name} conn={conn} db={db} schema={s} selected={selected} onSelect={onSelect} />)}
    </Node>
  );
}

function SchemaNode({ conn, db, schema, selected, onSelect }) {
  const loadTables = useCallback(async () => schema.tables, [schema]);
  return (
    <Node depth={2} icon={<Icon name="schema" className="ic-schema" />} label={schema.name}
          badge={<span className="cx-count">{schema.tables.length}</span>} load={loadTables}>
      {(tables) => tables.map((t) => {
        const active = selected && selected.conn.id === conn.id && selected.database === db &&
          selected.schema === schema.name && selected.table === t.name;
        const ic = t.type === "VIEW" ? "view" : t.type === "COLLECTION" ? "collection" : "table";
        return (
          <Node key={t.name} depth={3} icon={<Icon name={ic} className="ic-table" />} label={t.name}
                badge={t.type === "VIEW" ? <span className="cx-count">view</span> : null} active={active}
                onClick={() => onSelect({ conn, database: db, schema: schema.name, table: t.name })} />
        );
      })}
    </Node>
  );
}

function TableDetail({ sel, onImported }) {
  const { conn, database, schema, table } = sel;
  const [tab, setTab] = useState("columns");
  const [cols, setCols] = useState(null);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [imp, setImp] = useState({ target: suggestTarget(conn.name, table), maxRows: 200000 });
  const [job, setJob] = useState(null);
  const [impErr, setImpErr] = useState("");

  useEffect(() => {
    let live = true;
    api.getConnectionColumns(conn.id, database, schema, table)
      .then((r) => live && setCols(r.columns)).catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [conn.id, database, schema, table]);

  useEffect(() => {
    if (tab !== "data" || rows) return undefined;
    let live = true;
    api.previewConnectionTable(conn.id, database, schema, table)
      .then((r) => live && setRows(r)).catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [tab, rows, conn.id, database, schema, table]);

  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const t = setInterval(async () => {
      const now = ((await api.listImports()).imports || []).find((i) => i.id === job.id);
      if (now) setJob(now);
      if (now && now.status !== "running") onImported();
    }, 1200);
    return () => clearInterval(t);
  }, [job, onImported]);

  async function startImport(e) {
    e.preventDefault();
    setImpErr("");
    try {
      setJob(await api.startImport(conn.id, { database, schema, table, target: imp.target, maxRows: Number(imp.maxRows) }));
      onImported();
    } catch (er) {
      setImpErr(er.message);
    }
  }

  const tabs = [["columns", "Columns"], ["data", "Sample data"]];
  if (!conn.builtin) tabs.push(["import", "Import into QLens"]);

  return (
    <div className="cx-detail-in">
      <div className="cx-detail-h">
        <span className="cx-detail-ic"><Icon name="table" size={20} /></span>
        <div>
          <div className="cx-detail-title">{table}</div>
          <div className="cx-muted">{conn.name} › {database} › {schema}{cols ? ` · ${cols.length} columns` : ""}</div>
        </div>
        {conn.builtin && <span className="cx-pill builtin">In QLens</span>}
      </div>
      <div className="cx-tabs">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" className={"cx-tab" + (tab === k ? " on" : "")} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
      {err && tab !== "import" && <div className="cx-msgbox bad">{err}</div>}
      {tab === "columns" && (cols ? (
        <div className="cx-scroll">
          <table className="cx-table">
            <thead><tr><th>Column</th><th>Type</th><th>Nullable</th><th>Key</th>{cols[0]?.presence != null && <th>In documents</th>}</tr></thead>
            <tbody>
              {cols.map((c) => (
                <tr key={c.name}>
                  <td className="cx-strong">{c.name}</td><td><span className="cx-type">{c.type}</span></td>
                  <td>{c.nullable ? "Yes" : "No"}</td><td>{c.primaryKey && <span className="cx-pill pk">PK</span>}</td>
                  {c.presence != null && <td>{c.presence}%</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !err && <div className="cx-loading"><Spinner animation="border" size="sm" /></div>)}
      {tab === "data" && (rows ? (
        <>
          <div className="cx-scroll">
            <table className="cx-table">
              <thead><tr>{rows.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {rows.rows.map((r, i) => (
                  <tr key={i}>{r.map((v, j) => <td key={j}>{v === null ? <span className="cx-null">null</span> : String(v)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cx-muted cx-note">First {rows.rows.length} rows · read-only</p>
        </>
      ) : !err && <div className="cx-loading"><Spinner animation="border" size="sm" /></div>)}
      {tab === "import" && (
        <form className="cx-import" onSubmit={startImport}>
          <p className="cx-muted">Copies this table into QLens so you can ask questions about it, chart it and add it to dashboards. The source is only read; refresh any time.</p>
          <div className="cx-grid">
            <label className="cx-field">
              <span>Name in QLens</span>
              <input value={imp.target} onChange={(e) => setImp({ ...imp, target: e.target.value })} pattern="ext_[a-z0-9_]{1,55}" required />
            </label>
            <label className="cx-field">
              <span>Max rows</span>
              <input type="number" min={1} max={200000} value={imp.maxRows} onChange={(e) => setImp({ ...imp, maxRows: e.target.value })} />
            </label>
          </div>
          {impErr && <div className="cx-msgbox bad">{impErr}</div>}
          {job?.status === "running" && <div className="cx-msgbox"><Spinner size="sm" animation="border" /> Importing…</div>}
          {job?.status === "ok" && (
            <div className="cx-msgbox ok"><Icon name="check" />Imported {fmtNum(job.rows)} rows as <code>{job.table}</code>{job.truncated ? " (capped)" : ""}. Ready in Chat now; grant access on the Roles page.</div>
          )}
          {job?.status === "failed" && <div className="cx-msgbox bad">Import failed: {job.error}</div>}
          <div><button type="submit" className="cx-btn primary" disabled={job?.status === "running"}>{job?.status === "ok" ? "Import again" : "Import"}</button></div>
        </form>
      )}
    </div>
  );
}
